import * as React from "react";

import clsx from "clsx";
import { useEffect } from "react";
import { uniqueId } from "lodash-es";
import { isEmptyOrWhitespace, isNullOrUndefined, KeyValuePair } from "@shoothill/core";
import { Box, ICommand, theme } from "Application";
import { Label } from "../Labels/Label";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { RadioContainer } from "./RadioButton.styles";

interface Props {
    horizontal: boolean;
}

// #region View Props
interface IProps {
    className?: string;
    displayName?: string;
    command: ICommand;
    options: KeyValuePair[];
    value: () => string | boolean;
    horizontal?: boolean;
    labelPosition?: "top" | "left";
    validationMessage?: () => string;
    ref?: React.Ref<HTMLInputElement>;
    cy?: string;
    name?: string;
}
// #endregion View Props

export const RadioButton: React.FC<IProps> = observer((props) => {
    let nameId: string = uniqueId("radio-");

    // #region Public Interface
    useEffect(() => {}, []);

    // #region Code Behind

    const getClassName = (): string => {
        return clsx({
            radiobutton: true,
            [`radiobutton-${props.className}`]: !isEmptyOrWhitespace(props.className),
        });
    };

    const isDisabled = (): boolean => {
        return isNullOrUndefined(props.command.canExecute) ? false : !props.command.canExecute;
    };

    const isChecked = (key: string | boolean): boolean => {
        return key === props.value();
    };

    const onChange = (key: string | boolean): void => {
        props.command.execute(key);
    };
    const getDataCY = (): string => {
        let regex = /[^a-z]/gi;
        let result = (props.displayName || "").replace(regex, "");
        return props.cy || result;
    };
    // #endregion Code Behind

    const labelPosition = props.labelPosition ?? "top";

    return (
        <Box
            style={{
                display: "flex",
                flexDirection: labelPosition === "left" ? "row" : "column",
                gap: labelPosition === "left" ? "15px" : "0",
                alignItems: labelPosition === "left" ? "center" : "stretch",
            }}
        >
            {props.displayName && (
                <Box flexBox style={{ flex: labelPosition === "left" ? "0 0 auto" : "1 1 100%" }}>
                    <Label fontStyle={"label"} style={{ display: "block", marginBottom: labelPosition === "top" ? "10px" : 0 }}>
                        {props.displayName}
                    </Label>
                </Box>
            )}
            <RadioContainer horizontal={props.horizontal ?? true} style={{ flex: labelPosition === "left" ? "1 1 auto" : "1 1 100%" }}>
                {props.options.map((option: KeyValuePair, index: number) => {
                    const htmlForId = `radio-${index}-${option.key}${props?.name ?? ""}`;
                    return (
                        <div className={"radio"} key={htmlForId}>
                            <input
                                ref={props.ref}
                                name={nameId}
                                checked={isChecked(option.key)}
                                disabled={isDisabled()}
                                id={htmlForId}
                                onChange={() => onChange(option.key)}
                                type="radio"
                                data-cy={getDataCY()}
                            />
                            <Label fontStyle={"radioButtonLabel"} htmlFor={htmlForId} style={{ display: "inline", whiteSpace: "nowrap" }} className={"radio-label"}>
                                {option.text}
                            </Label>
                        </div>
                    );
                })}
            </RadioContainer>
        </Box>
    );
    // #endregion Public Interface
});
RadioButton.defaultProps = {
    horizontal: true,
};
